import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ButtonAddNew from "../../buttons/ButtonAddNew";
import SortBy from "./SortBy";
import Search from "../header/Search";
import ModalCreateFolder from "../../modal/modalCreateFolder";
import ButtonDelete from "../../buttons/ButtonDelete";
import ButtonDownload from "../../buttons/ButtonDownload";
import ItemsSelected from "../../buttons/ItemsSelected";
import ModalUploadFile from "../../modal/modalUploadFile";
import ModalDeleteFile from "../../modal/modalDeleteFile";
import { toast } from "react-toastify";
import useFileManagerApi from "../../../hooks/api/useFileManagerApi";

const NavigationMyFiles = ({ selectedFolders, onSortChange, onFilesChanged, onClearSelection, folderStructure = null, onFileClick = null }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { deleteFiles, downloadBinaryByEncodedPath } = useFileManagerApi();

  const sortOptions = ["Name (A-Z)", "Type", "File Size", "Date (newest)", "Date (oldest)"];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible]);

  const getCurrentPath = () => {
    return params["*"] || "";
  };

  const ensureItemObject = (entry) => {
    if (!entry) return null;
    if (typeof entry === "string") {
      const clean = entry.replace(/\/+$/, "");
      return { id: clean, name: clean, type: "file" };
    }
    return {
      ...entry,
      id: entry.id || entry.name || entry.path || entry.fullPath || `item-${Date.now()}-${Math.random()}`,
      name: entry.name || entry.id || "",
      type: entry.type || entry.itemType || (entry.isFolder ? "folder" : "file"),
      path: entry.path || entry.fullPath || entry.id || entry.name || "",
    };
  };

  const isFolderItem = (item) =>
    Boolean(
      item &&
        (item.type === "folder" ||
          item.itemType === "folder" ||
          item.isFolder === true ||
          (item.path && /\/$/.test(item.path)) ||
          (item.fullPath && /\/$/.test(item.fullPath)))
    );

  const resolveRelativePath = (item, basePathOverride = null) => {
    if (!item) return "";
    const explicitPath = item.fullPath || item.path;
    if (explicitPath) {
      return explicitPath.replace(/^\/+/, "");
    }

    const basePath = (basePathOverride ?? getCurrentPath()).replace(/\/+$/, "");
    const identifier = (item.id || item.name || "").replace(/\/+$/, "");

    if (!identifier) {
      return basePath;
    }

    return basePath ? `${basePath}/${identifier}` : identifier;
  };

  const normalizeSelectionItems = (entries, basePathOverride = null) => {
    if (!Array.isArray(entries)) return [];
    return entries
      .map((entry) => {
        const prepared = ensureItemObject(entry);
        if (!prepared) return null;
        return {
          ...prepared,
          path: resolveRelativePath(prepared, basePathOverride),
        };
      })
      .filter(Boolean);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setIsVisible(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedItems([]);
  };

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  const handleDeleteButtonClick = () => {
    if (!selectedFolders || !Array.isArray(selectedFolders) || selectedFolders.length === 0) {
      toast.error("No files selected");
      return;
    }

    const items = normalizeSelectionItems(selectedFolders, getCurrentPath());
    setSelectedItems(items);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const targets =
      selectedItems.length > 0
        ? selectedItems
        : normalizeSelectionItems(selectedFolders, getCurrentPath());

    if (!targets || targets.length === 0) {
      toast.error("No files selected");
      return;
    }

    setIsDeleting(true);

    try {
      const filePaths = targets
        .map((item) => {
          const relativePath = (item.path || resolveRelativePath(item) || "").replace(/^\/+/, "");
          if (!relativePath) {
            return null;
          }

          const trimmedPath = relativePath.replace(/\/+$/, "");
          return isFolderItem(item) ? `${trimmedPath}/` : trimmedPath;
        })
        .filter(Boolean);

      const response = await deleteFiles(filePaths);

      if (response.status === 200) {
        toast.success(
          targets.length === 1
            ? "File deleted successfully"
            : `${targets.length} files deleted successfully`
        );
        setIsDeleteModalOpen(false);
        setSelectedItems([]);

        if (onFilesChanged) {
          onFilesChanged();
        }

        if (onClearSelection) {
          onClearSelection();
        }
      }
    } catch (error) {
      console.error("Delete error:", error);

      if (error.response) {
        const { status, data } = error.response;

        if (status === 404) {
          toast.error("Files not found");
        } else if (status === 403) {
          toast.error("You don't have permission to delete these files");
        } else {
          toast.error(data?.error || "Error deleting files");
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your internet connection.");
      } else {
        toast.error("An unexpected error occurred while deleting files");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadButtonClick = async () => {
    if (!selectedFolders || !Array.isArray(selectedFolders) || selectedFolders.length === 0) {
      toast.error("No files selected");
      return;
    }

    const items = normalizeSelectionItems(selectedFolders, getCurrentPath());

    for (const item of items) {
      try {
        let relativePath = (item.path || "").replace(/^\/+/, "");
        if (!relativePath) {
          relativePath = resolveRelativePath(item);
        }

        let normalizedPath = relativePath.startsWith("/")
          ? relativePath
          : `/${relativePath}`;

        if (!normalizedPath.endsWith("/")) {
          normalizedPath += "/";
        }
        const encodedPath = encodeURIComponent(normalizedPath);

        const response = await downloadBinaryByEncodedPath(encodedPath);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", item.name || item.id);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(`${item.name || item.id} downloaded successfully`);
      } catch (error) {
        console.error(`Download error for ${item?.name || item?.id}:`, error);

        if (error.response) {
          const { status } = error.response;

          if (status === 404) {
            toast.error(`${item?.name || item?.id} not found`);
          } else if (status === 403) {
            toast.error(`You don't have permission to download ${item?.name || item?.id}`);
          } else {
            toast.error(`Error downloading ${item?.name || item?.id}`);
          }
        } else {
          toast.error(`Error downloading ${item?.name || item?.id}`);
        }
      }
    }
  };

  const handleUploadModalOpen = () => {
    setIsUploadModalOpen(true);
    setIsVisible(false);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSuccess = () => {
    if (onFilesChanged) {
      onFilesChanged();
    }
  };

  const selectedCount = Array.isArray(selectedFolders) ? selectedFolders.length : 0;
  const hasSelectedFiles = selectedCount > 0;
  const pendingDeletionCount = selectedItems.length || selectedCount;

  return (
    <div>
      <div className="title-and-button mobile:bg-white mobile:py-3 space-y-6 py-4 border-b border-gray-200">
        <div className="flex flex-row justify-between mobile:px-4 px-6 items-center">
          <div className="flex basis-1/4">
            <h6 className="text-dark4 text-h6 mobile:text-bodyLM cursor-pointer whitespace-nowrap">
              <span onClick={() => navigate("/dashboard/my-files")}>
                My Files
              </span>
            </h6>
          </div>

          {hasSelectedFiles && (
            <div className="buttons-action flex items-center flex-row mobile:space-x-6 desktopXl:space-x-12 mobile:hidden">
              <ButtonDownload
                className="border-0"
                downloadAction={handleDownloadButtonClick}
              />
              <ButtonDelete
                className="border-0"
                deleteAction={handleDeleteButtonClick}
              />
              <ItemsSelected
                className="rounded-xl whitespace-nowrap"
                selectedFoldersLength={selectedCount}
              />
            </div>
          )}

          <div className="flex flex-row space-x-6">
            <SortBy
              textSort="Sort by"
              className="mobile:hidden"
              items={sortOptions}
              onSortChange={onSortChange}
            />
            <div className="relative cursor-pointer">
              <ButtonAddNew
                textButton="Add new"
                onClick={toggleVisibility}
                className="mobile:px-[8px] mobile:py-[8px]"
              />
              {isVisible && (
                <div
                  ref={dropdownRef}
                  className="bg-white border cursor-pointer absolute w-[200px] right-[0px] rounded-lg mt-2 z-20 shadow-lg"
                >
                  <button
                    className="w-full text-bodyMR text-dark4 hover:text-white hover:bg-primary px-3 py-2 flex items-center space-x-2 rounded-t-lg"
                    onClick={handleOpenModal}
                  >
                    <svg width="20" height="18" viewBox="0 0 20 18" fill="currentColor">
                      <path
                        d="M2.49963 1.1001H8.30334C8.72747 1.10041 9.1344 1.26883 9.4342 1.56885L11.1305 3.26611H11.1315C11.2059 3.34035 11.294 3.39986 11.3912 3.43994C11.4882 3.4799 11.5921 3.49959 11.6969 3.49951V3.50049H17.4996C17.9239 3.50049 18.3314 3.66833 18.6315 3.96826C18.9315 4.26832 19.1002 4.67575 19.1002 5.1001V15.3003C19.1002 15.7245 18.9314 16.1312 18.6315 16.4312C18.3314 16.7312 17.924 16.8999 17.4996 16.8999H2.49963C1.61682 16.8997 0.900131 16.1831 0.900024 15.3003V2.69971C0.900128 2.2755 1.06881 1.86881 1.36877 1.56885C1.66874 1.26888 2.07543 1.1002 2.49963 1.1001ZM9.99963 6.1001C9.62852 6.1002 9.27282 6.24786 9.01038 6.51025C8.74782 6.7728 8.60022 7.12919 8.60022 7.50049V8.80029H7.30042C6.92911 8.80029 6.57273 8.9479 6.31018 9.21045C6.04778 9.4729 5.90013 9.82859 5.90002 10.1997C5.90002 10.571 6.04763 10.9274 6.31018 11.1899C6.57273 11.4525 6.92911 11.6001 7.30042 11.6001H8.60022V12.8999C8.60022 13.2712 8.74782 13.6276 9.01038 13.8901C9.27282 14.1525 9.62856 14.3002 9.99963 14.3003C10.3709 14.3003 10.7273 14.1526 10.9899 13.8901C11.2524 13.6276 11.4 13.2712 11.4 12.8999V11.6001H12.6998C13.0711 11.6001 13.4275 11.4525 13.6901 11.1899C13.9526 10.9274 14.1002 10.571 14.1002 10.1997C14.1001 9.82864 13.9524 9.47289 13.6901 9.21045C13.4275 8.9479 13.0711 8.80029 12.6998 8.80029H11.4V7.50049C11.4 7.12919 11.2524 6.7728 10.9899 6.51025C10.7273 6.2477 10.3709 6.1001 9.99963 6.1001Z"
                        fill="currentColor"
                        stroke="currentColor"
                      />
                    </svg>
                    <span>New folder</span>
                  </button>
                  <button
                    className="w-full text-bodyMR text-dark4 hover:text-white hover:bg-primary px-3 py-2 flex items-center space-x-2 rounded-b-lg"
                    onClick={handleUploadModalOpen}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M14.1522 2.9985C14.3101 3.06375 14.4535 3.15946 14.5743 3.28017C14.5744 3.28024 14.5745 3.2803 14.5745 3.28037L19.5183 8.22657L19.5186 8.22688C19.7625 8.47044 19.8997 8.80088 19.9 9.14554V19.7999C19.9 20.1447 19.763 20.4753 19.5192 20.7191C19.2754 20.9629 18.9448 21.0999 18.6 21.0999H5.39998C5.05519 21.0999 4.72453 20.9629 4.48074 20.7191C4.23694 20.4753 4.09998 20.1447 4.09998 19.7999V4.1999C4.09998 3.85512 4.23694 3.52446 4.48074 3.28066C4.72453 3.03687 5.05519 2.8999 5.39998 2.8999L13.6536 2.8999L13.6541 2.8999C13.825 2.89971 13.9943 2.93322 14.1522 2.9985ZM13.4 11.7999V10.4999C13.4 10.1286 13.2525 9.7725 12.9899 9.50995C12.7274 9.2474 12.3713 9.0999 12 9.0999C11.6287 9.0999 11.2726 9.2474 11.01 9.50995C10.7475 9.7725 10.6 10.1286 10.6 10.4999V11.7999H9.29998C8.92867 11.7999 8.57258 11.9474 8.31003 12.21C8.04747 12.4725 7.89998 12.8286 7.89998 13.1999C7.89998 13.5712 8.04747 13.9273 8.31003 14.1899C8.57258 14.4524 8.92867 14.5999 9.29998 14.5999H10.6V15.8999C10.6 16.2712 10.7475 16.6273 11.01 16.8899C11.2726 17.1524 11.6287 17.2999 12 17.2999C12.3713 17.2999 12.7274 17.1524 12.9899 16.8899C13.2525 16.6273 13.4 16.2712 13.4 15.8999V14.5999H14.7C15.0713 14.5999 15.4274 14.4524 15.6899 14.1899C15.9525 13.9273 16.1 13.5712 16.1 13.1999C16.1 12.8286 15.9525 12.4725 15.6899 12.21C15.4274 11.9474 15.0713 11.7999 14.7 11.7999H13.4Z"
                        fill="currentColor"
                        stroke="currentColor"
                      />
                    </svg>
                    <span>New file</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between gap-3 px-4 items-center desktop:hidden">
          <div className="flex-1">
            <Search
              onMobileActiveChange={setIsMobileSearchActive}
              folderStructure={folderStructure}
              onFileClick={onFileClick}
            />
          </div>
          {!isMobileSearchActive && (
            <div className="desktop:hidden">
              <SortBy
                textSort="Sort by"
                className=""
                items={sortOptions}
                onSortChange={onSortChange}
              />
            </div>
          )}
        </div>

        {hasSelectedFiles && (
          <div className="buttons-action flex px-4 items-center justify-between desktop:hidden w-full">
            <div className="flex flex-1 gap-3 items-center mobile:gap-2 justify-between">
              <ButtonDownload
                className="border-0 w-full p-0"
                downloadAction={handleDownloadButtonClick}
              />
              <ButtonDelete
                className="border-0 w-full p-0"
                deleteAction={handleDeleteButtonClick}
              />
              <div className="w-full max-w-[120px]">
                <ItemsSelected
                  className="rounded-xl mobile:px-[4px] mobile:py-[4px] w-full justify-center"
                  selectedFoldersLength={selectedCount}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalCreateFolder
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedFolder={selectedFolders}
        currentPath={getCurrentPath()}
        onFolderCreated={onFilesChanged}
      />

      <ModalUploadFile
        selectedFolder={getCurrentPath()}
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        onUploadSuccess={handleUploadSuccess}
      />

      <ModalDeleteFile
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        item={{
          id: "multiple",
          name:
            pendingDeletionCount === 1
              ? "selected item"
              : `${pendingDeletionCount} selected items`,
          type: "multiple",
        }}
        onDelete={handleConfirmDelete}
        isDeleting={isDeleting}
        messageWarning={
          pendingDeletionCount > 1
            ? `You are about to delete ${pendingDeletionCount} items. This action cannot be undone.`
            : null
        }
      />
    </div>
  );
};

export default NavigationMyFiles;